import { useTemplateFolders } from "@/queries/forms/template-folder.query.ts";
import { TemplateFolderModule } from "@/types/forms/form-template-folder.types.ts";
import { ApiFormTemplateSavePayload } from "@/types/forms/form-template.types.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import {
  Button,
  useToasts,
  Input,
  useModals,
  Modal,
  ModalProps,
  Stack,
  Select,
  Text,
} from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useEffect, useState } from "react";

export interface FormSaveTemplateModalProps extends ModalProps {
  module: TemplateFolderModule;
  onSave: (payload: ApiFormTemplateSavePayload) => Promise<void>;
}

export function FormSaveTemplateModal({ module, onSave, ...props }: FormSaveTemplateModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };
  const [isSaving, setIsSaving] = useState(false);
  const { showInfo } = useToasts();
  const { data: folders, isLoading, isError } = useTemplateFolders(module);

  const schema = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    folder_id: z
      .number({ error: i18n.t("errors:field_required") })
      .refine((id) => folders?.some((folder) => folder.id === id), i18n.t("errors:field_required")),
  });
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (folders?.length && getValues("folder_id") === undefined) {
      setValue("folder_id", folders[0].id);
    }
  }, [folders, getValues, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave(data);
      showInfo({ title: t("surveys:template_saved") });
      close(id);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Modal
      {...props}
      title={t("common:template")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            onClick={onSubmit}
            isLoading={isSaving}
            disabled={isLoading || isError || !folders?.length}
          >
            {t("surveys:create_template")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />
        <Controller
          control={control}
          name="folder_id"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("core:folder")}
              emptyTitle={t("common:select")}
              value={value}
              onChange={onChange}
              options={folders}
              isLoading={isLoading}
              required
              error={errors.folder_id?.message}
            />
          )}
        />
        {isError && <Text className="text-sm text-danger">{t("common:folders_load_failed")}</Text>}
      </form>
    </Modal>
  );
}
